"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { LogoutButton } from "@/features/auth";
import { Brand } from "./Brand";
import { MobileNavigation } from "./MobileNavigation";
import { Navigation } from "./Navigation";
import styles from "./AppShell.module.css";

type AppShellProps = {
  children: ReactNode;
};

const SIDEBAR_COLLAPSED_STORAGE_KEY = "trading-platform.sidebar-collapsed";
const SIDEBAR_STORAGE_EVENT = "sidebar-storage-change";

function getSidebarCollapsedSnapshot() {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function syncSidebarDocumentState() {
  document.documentElement.dataset.sidebarCollapsed = String(getSidebarCollapsedSnapshot());
}

function subscribeToSidebarStorage(onStoreChange: () => void) {
  function handleStorage(event: StorageEvent) {
    if (event.key === SIDEBAR_COLLAPSED_STORAGE_KEY) {
      syncSidebarDocumentState();
      onStoreChange();
    }
  }

  function handleLocalStorageChange() {
    syncSidebarDocumentState();
    onStoreChange();
  }

  window.addEventListener("storage", handleStorage);
  window.addEventListener(SIDEBAR_STORAGE_EVENT, handleLocalStorageChange);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(SIDEBAR_STORAGE_EVENT, handleLocalStorageChange);
  };
}

export function AppShell({ children }: AppShellProps) {
  const isSidebarCollapsed = useSyncExternalStore(
    subscribeToSidebarStorage,
    getSidebarCollapsedSnapshot,
    () => false,
  );

  const toggleSidebar = useCallback(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(!isSidebarCollapsed));
      window.dispatchEvent(new Event(SIDEBAR_STORAGE_EVENT));
    } catch {
      // Keep the current state when storage is unavailable.
    }
  }, [isSidebarCollapsed]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target;
      const isEditable =
        target instanceof HTMLElement &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

      if (event.key.toLowerCase() === "b" && (event.metaKey || event.ctrlKey) && !isEditable) {
        event.preventDefault();
        toggleSidebar();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  return (
    <div className={styles.shell} data-sidebar-collapsed={isSidebarCollapsed ? "true" : "false"}>
      <header className={styles.desktopHeader}>
        <Brand />
        <LogoutButton className={styles.logoutButton} />
      </header>

      <Navigation variant="desktop" isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
      <MobileNavigation />

      <main className={styles.main}>
        <div className={styles.mainContent}>{children}</div>
      </main>
    </div>
  );
}
